import React from 'react';
import { Award, Users, Calendar, Shield, CheckCircle } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Calendar, value: '20+', label: 'Years Experience' },
    { icon: Users, value: '800+', label: 'Happy Clients' },
    { icon: Award, value: '15+', label: 'Certifications' },
    { icon: Shield, value: '100%', label: 'Safety Rating' }
  ];

  const certifications = [
    'APP (Association of Professional Piercers) Member',
    'Bloodborne Pathogen Certification',
    'CPR & First Aid Certified',
    'Autoclave Sterilization Training',
    'Anatomy & Physiology Certification'
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section with Flyer */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-black mb-4 text-zinc-100">ABOUT STEVE</h1>
            <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
              Master body piercer with over two decades of experience in safe, artistic body modification
            </p>
          </div>

          {/* Flyer Image */}
          <div className="max-w-3xl mx-auto mb-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_stabbed-by-steve/artifacts/8pbkyxou_flyer.jpg" 
              alt="Stabbed By Steve - Professional Piercing" 
              className="w-full border-2 border-zinc-800 shadow-2xl"
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                  <Icon className="h-10 w-10 text-red-800 mx-auto mb-3" />
                  <div className="text-4xl font-black text-zinc-100 mb-2">{stat.value}</div>
                  <div className="text-zinc-400 font-semibold">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-zinc-950 border border-zinc-800 p-8 md:p-12">
            <h2 className="text-3xl font-black text-zinc-100 mb-6 border-l-4 border-red-800 pl-4">
              Steve - Professional Piercer
            </h2>
            
            <div className="space-y-6 text-zinc-300 leading-relaxed text-lg">
              <p>
                With over <span className="text-red-800 font-bold">20 years of experience</span> in the body modification industry, Steve has established himself as one of the most trusted and skilled certified body piercers. Specializing in surface piercings, Steve has mastered the art of making typically complex, hard-to-keep piercings possible and permanent.
              </p>

              <p>
                Through expert technique, precise jewelry selection, placement analysis, and specialized aftercare protocols, Steve transforms surface piercings that are traditionally short-lived into lasting works of art. His deep understanding of anatomy and healing processes allows him to achieve what many consider impossible in surface piercing longevity.
              </p>

              <p>
                Steve's commitment to <span className="text-zinc-100 font-semibold">safety, precision, and artistry</span> has earned him a loyal clientele and recognition within the piercing community. Every piercing is performed with single-use medical-grade equipment in a sterile environment, ensuring the highest standards of safety and hygiene.
              </p>

              <p>
                Beyond technical expertise, Steve is passionate about education and aftercare support, providing thorough comprehensive guidance on at-home care and cleaning, to ensure optimal healing and long-term satisfaction for every client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-black text-zinc-100 mb-12 text-center">
            CERTIFICATIONS & TRAINING
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <div 
                key={index} 
                className="bg-zinc-900 border border-zinc-800 p-6 flex items-start gap-4 hover:border-red-800 transition-all duration-300"
              >
                <CheckCircle className="h-6 w-6 text-red-800 flex-shrink-0 mt-1" />
                <span className="text-zinc-100 font-semibold text-lg">{cert}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-red-950 border-2 border-red-900 p-8 text-center">
            <h3 className="text-2xl font-black text-zinc-100 mb-4">
              YOUR SAFETY IS MY PRIORITY
            </h3>
            <p className="text-zinc-300 leading-relaxed">
              All procedures are performed in a sterile environment using single-use, medical-grade equipment. 
              Every client receives comprehensive aftercare instructions and ongoing support throughout the healing process.
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-red-950 via-zinc-950 to-zinc-950">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-black text-zinc-100 mb-4">
            READY TO GET PIERCED?
          </h2>
          <p className="text-xl text-zinc-300 mb-8">
            Book your appointment with a master piercer
          </p>
          <a 
            href="/booking" 
            className="inline-block bg-red-800 hover:bg-red-700 text-white px-12 py-4 text-lg font-bold tracking-wide transition-all duration-300 hover:scale-105 shadow-xl shadow-red-900/50"
          >
            BOOK NOW
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
